import type { ComponentType } from 'react'
import type { SxProps, Theme } from '@mui/material/styles'
import lessonsRuntime, { collectAssetReferences as collectLessonReferences } from './lessons/runtime'
import lessonsSystemPrompt from './lessons/systemPrompt.txt?raw'
import chartsRuntime, { collectAssetReferences as collectChartReferences } from './charts/runtime'
import chartsSystemPrompt from './charts/systemPrompt.txt?raw'
import experimentsRuntime, { collectAssetReferences as collectExperimentReferences } from './experiments/runtime'
import experimentsSystemPrompt from './experiments/systemPrompt.txt?raw'
import ExperimentAssetRenderer from './experiments/ExperimentAssetRenderer'
import learningTemplatesRuntime, {
  collectAssetReferences as collectLearningTemplateReferences,
} from './learning_templates/runtime'
import learningTemplatesSystemPrompt from './learning_templates/systemPrompt.txt?raw'
import learningBatteriesRuntime, {
  collectAssetReferences as collectLearningBatteryReferences,
} from './learning_batteries/runtime'
import learningBatteriesSystemPrompt from './learning_batteries/systemPrompt.txt?raw'
import datasetsRuntime, { collectAssetReferences as collectDatasetReferences } from './datasets/runtime'
import datasetsSystemPrompt from './datasets/systemPrompt'
import dashboardsRuntime, { collectAssetReferences as collectDashboardReferences } from './dashboards/runtime'
import dashboardsSystemPrompt from './dashboards/systemPrompt.txt?raw'
import configsRuntime, { collectAssetReferences as collectConfigReferences } from './configs/runtime'
import configsSystemPrompt from './configs/systemPrompt.txt?raw'
import featuresRuntime, { collectAssetReferences as collectFeatureReferences } from './features/runtime'
import featuresSystemPrompt from './features/systemPrompt.txt?raw'
import FeatureAssetRenderer from './features/FeatureAssetRenderer'
import ConfigAssetRenderer from './configs/ConfigAssetRenderer'
import formsRuntime, { collectAssetReferences as collectFormReferences } from './forms/runtime'
import formsSystemPrompt from './forms/systemPrompt.txt?raw'
import FormAssetRenderer from './forms/FormAssetRenderer'
import pngRenderer, { collectAssetReferences as collectPngReferences } from './png/runtime'
import jpgRenderer, { collectAssetReferences as collectJpgReferences } from './jpg/runtime'
import svgRenderer, { collectAssetReferences as collectSvgReferences } from './svg/runtime'
import mp3Renderer, { collectAssetReferences as collectMp3References } from './mp3/runtime'
import mp4Renderer, { collectAssetReferences as collectMp4References } from './mp4/runtime'
import audioThumbnail from './audio/thumbnail.svg'
import videoThumbnail from './video/thumbnail.svg'
import lessonsThumbnail from './lessons/thumbnail.svg'
import datasetsThumbnail from './datasets/thumbnail.svg'
import learningTemplatesThumbnail from './learning_templates/thumbnail.svg'
import experimentsThumbnail from './experiments/thumbnail.svg'
import type { ParamChangeEvent, ParamValues } from './assetParamsBus'
import type { TrellisEntitiesApi } from './trellisEntities'

export type ContentLanguage = 'markdown' | 'yaml' | 'typescript'

export type AssetMediaType = 'image' | 'audio' | 'video'

export type RenderEntityField = {
  id: string
  name: string
  type: string
  value: string
  required: boolean
}

export type RenderEntitySnapshot = {
  id?: string
  typeId: string
  typeName: string
  fields: RenderEntityField[]
}

export type RenderAuthContext = {
  accessToken: string
  organizationId: string
  apiBaseUrl: string
  entityApiUrl: string
  userEntityId: string
}

export type RenderContext = {
  embedDepth?: number
}

export type RenderData = {
  entity: RenderEntitySnapshot
  auth?: RenderAuthContext
  context?: RenderContext
}

export type ReactAssetRuntime = {
  instanceId: string
  entity?: RenderEntitySnapshot
  assets: {
    get: (ref: string) => AssetHandle
  }
  entities?: TrellisEntitiesApi
  params: {
    get: () => ParamValues
    subscribe: (listener: (event: ParamChangeEvent) => void) => () => void
  }
  navigation?: ReactAssetNavigation
  features?: {
    Inline: ComponentType<InlineFeatureComponentProps>
  }
  log: (message: string) => void
  ui: {
    showLoading: (message?: string) => void
    hideLoading: () => void
    showError: (message?: string) => void
    clear: () => void
  }
}

export type ReactAssetFeatureTarget = {
  feature: string
  params?: ParamValues
}

export type ReactAssetNavigation = {
  push: (input: ReactAssetFeatureTarget) => void
  pop: () => void
  resetToRoot: () => void
  canPop: () => boolean
}

export type InlineFeatureComponentProps = ReactAssetFeatureTarget & {
  navigation?: ReactAssetNavigation
}

export type AssetRenderComponentProps = {
  content: string
  renderData?: RenderData
  trellis?: ReactAssetRuntime
  previewWidth?: number
  previewHeight?: number
  reloadToken?: number
  layout: 'fixed' | 'fill'
  showLog: boolean
  previewSx?: SxProps<Theme>
  onLog?: (message: string) => void
}

export type AssetHandle = {
  id: string
  mimeType: string
  mediaType: 'image' | 'audio' | 'video' | 'text'
  url: string
  text?: string
  datasetPayload?: { query?: unknown; params?: unknown }
}

export type RenderOptions = {
  data?: RenderData
  resolveAsset?: (ref: string) => { content: string; assetType?: AssetTypeDefinition } | undefined
}

export type AssetPayloadFormat = 'base64' | 'text'

export type AssetReferenceCollector = (content: string, renderData?: RenderData) => string[]

export type AssetBuildHtmlFn = (content: string, options?: RenderOptions) => string

export type InlineEmbeddingMode = 'trellis' | 'standalone'

export type AssetTypeDefinition = {
  id: string
  displayName: string
  payloadFormat: AssetPayloadFormat
  contentLanguage?: ContentLanguage
  mediaType?: AssetMediaType
  mimeType?: string
  supportedReferenceTypeIds?: string[]
  defaultThumbnailSrc?: string
  previewSize?: { width: number; height: number }
  buildHtml?: AssetBuildHtmlFn
  collectAssetReferences?: AssetReferenceCollector
  renderComponent?: ComponentType<AssetRenderComponentProps>
  systemPrompt?: string
  inlineEmbedding?: {
    mode: InlineEmbeddingMode
  }
}

const simpleAssetTypes: AssetTypeDefinition[] = [
  {
    id: 'png',
    displayName: 'PNG',
    payloadFormat: 'base64',
    mediaType: 'image',
    mimeType: 'image/png',
    buildHtml: pngRenderer,
    collectAssetReferences: collectPngReferences,
  },
  {
    id: 'jpg',
    displayName: 'JPG',
    payloadFormat: 'base64',
    mediaType: 'image',
    mimeType: 'image/jpeg',
    buildHtml: jpgRenderer,
    collectAssetReferences: collectJpgReferences,
  },
  {
    id: 'svg',
    displayName: 'SVG',
    payloadFormat: 'base64',
    mediaType: 'image',
    mimeType: 'image/svg+xml',
    buildHtml: svgRenderer,
    collectAssetReferences: collectSvgReferences,
  },
  {
    id: 'mp3',
    displayName: 'MP3',
    payloadFormat: 'base64',
    mediaType: 'audio',
    mimeType: 'audio/mpeg',
    defaultThumbnailSrc: audioThumbnail,
    buildHtml: mp3Renderer,
    collectAssetReferences: collectMp3References,
  },
  {
    id: 'mp4',
    displayName: 'MP4',
    payloadFormat: 'base64',
    mediaType: 'video',
    mimeType: 'video/mp4',
    defaultThumbnailSrc: videoThumbnail,
    buildHtml: mp4Renderer,
    collectAssetReferences: collectMp4References,
  },
]

const mediaReferenceTypeIds = ['svg', 'png', 'jpg', 'mp3', 'mp4']
const datasetReferenceTypeIds = ['dataset', ...mediaReferenceTypeIds]
const experimentReferenceTypeIds = ['config', 'feature', 'form', ...mediaReferenceTypeIds]

const lessonsType: AssetTypeDefinition = {
  id: 'lesson',
  displayName: 'Lessons',
  payloadFormat: 'text',
  contentLanguage: 'markdown',
  supportedReferenceTypeIds: mediaReferenceTypeIds,
  systemPrompt: lessonsSystemPrompt,
  buildHtml: lessonsRuntime,
  collectAssetReferences: collectLessonReferences,
  defaultThumbnailSrc: lessonsThumbnail,
  previewSize: { width: 315, height: 560 },
}

const chartsType: AssetTypeDefinition = {
  id: 'chart',
  displayName: 'Charts',
  payloadFormat: 'text',
  contentLanguage: 'yaml',
  supportedReferenceTypeIds: datasetReferenceTypeIds,
  systemPrompt: chartsSystemPrompt,
  buildHtml: chartsRuntime,
  collectAssetReferences: collectChartReferences,
}

const experimentsType: AssetTypeDefinition = {
  id: 'experiment',
  displayName: 'Experiments',
  payloadFormat: 'text',
  contentLanguage: 'yaml',
  supportedReferenceTypeIds: experimentReferenceTypeIds,
  systemPrompt: experimentsSystemPrompt,
  buildHtml: experimentsRuntime,
  collectAssetReferences: collectExperimentReferences,
  renderComponent: ExperimentAssetRenderer,
  defaultThumbnailSrc: experimentsThumbnail,
  previewSize: { width: 560, height: 900 },
}

const learningTemplatesType: AssetTypeDefinition = {
  id: 'learning_template',
  displayName: 'Learning Templates',
  payloadFormat: 'text',
  contentLanguage: 'typescript',
  supportedReferenceTypeIds: mediaReferenceTypeIds,
  systemPrompt: learningTemplatesSystemPrompt,
  buildHtml: learningTemplatesRuntime,
  collectAssetReferences: collectLearningTemplateReferences,
  defaultThumbnailSrc: learningTemplatesThumbnail,
}

const learningBatteriesType: AssetTypeDefinition = {
  id: 'learning_battery',
  displayName: 'Learning Battery',
  payloadFormat: 'text',
  contentLanguage: 'yaml',
  supportedReferenceTypeIds: ['learning_template', ...mediaReferenceTypeIds],
  systemPrompt: learningBatteriesSystemPrompt,
  buildHtml: learningBatteriesRuntime,
  collectAssetReferences: collectLearningBatteryReferences,
}

const datasetsType: AssetTypeDefinition = {
  id: 'dataset',
  displayName: 'Datasets',
  payloadFormat: 'text',
  contentLanguage: 'yaml',
  supportedReferenceTypeIds: mediaReferenceTypeIds,
  buildHtml: datasetsRuntime,
  collectAssetReferences: collectDatasetReferences,
  systemPrompt: datasetsSystemPrompt,
  defaultThumbnailSrc: datasetsThumbnail,
  previewSize: { width: 560, height: 560 },
}

const dashboardsType: AssetTypeDefinition = {
  id: 'dashboard',
  displayName: 'Dashboards',
  payloadFormat: 'text',
  contentLanguage: 'yaml',
  buildHtml: dashboardsRuntime,
  collectAssetReferences: collectDashboardReferences,
  systemPrompt: dashboardsSystemPrompt,
  previewSize: { width: 560, height: 560 },
}

const featuresType: AssetTypeDefinition = {
  id: 'feature',
  displayName: 'Feature',
  payloadFormat: 'text',
  contentLanguage: 'typescript',
  buildHtml: featuresRuntime,
  collectAssetReferences: collectFeatureReferences,
  renderComponent: FeatureAssetRenderer,
  systemPrompt: featuresSystemPrompt,
  inlineEmbedding: { mode: 'trellis' },
  previewSize: { width: 560, height: 900 },
}

const formsType: AssetTypeDefinition = {
  id: 'form',
  displayName: 'Forms',
  payloadFormat: 'text',
  contentLanguage: 'yaml',
  supportedReferenceTypeIds: mediaReferenceTypeIds,
  buildHtml: formsRuntime,
  collectAssetReferences: collectFormReferences,
  renderComponent: FormAssetRenderer,
  systemPrompt: formsSystemPrompt,
  inlineEmbedding: { mode: 'standalone' },
  previewSize: { width: 315, height: 560 },
}

const configsType: AssetTypeDefinition = {
  id: 'config',
  displayName: 'Configs',
  payloadFormat: 'text',
  contentLanguage: 'yaml',
  buildHtml: configsRuntime,
  collectAssetReferences: collectConfigReferences,
  renderComponent: ConfigAssetRenderer,
  systemPrompt: configsSystemPrompt,
  previewSize: { width: 560, height: 900 },
}

const assetTypes: AssetTypeDefinition[] = [
  ...simpleAssetTypes,
  lessonsType,
  chartsType,
  experimentsType,
  learningTemplatesType,
  learningBatteriesType,
  datasetsType,
  dashboardsType,
  configsType,
  featuresType,
  formsType,
]

const normalizeLabel = (value: string) => value.trim().toLowerCase()
const toSingular = (value: string) => (value.endsWith('s') ? value.slice(0, -1) : value)

export const findAssetTypeByLabel = (label: string): AssetTypeDefinition | undefined => {
  const normalizedLabel = normalizeLabel(label)
  return assetTypes.find((assetType) => {
    const id = normalizeLabel(assetType.id)
    const displayName = normalizeLabel(assetType.displayName)
    if (id === normalizedLabel || displayName === normalizedLabel) return true
    const normalizedSingular = toSingular(normalizedLabel)
    return toSingular(id) === normalizedSingular || toSingular(displayName) === normalizedSingular
  })
}

export const findAssetTypeById = (assetTypeId: string): AssetTypeDefinition | undefined =>
  assetTypes.find((assetType) => assetType.id === assetTypeId)

export const getInlineEmbeddingMode = (assetType?: AssetTypeDefinition): InlineEmbeddingMode | undefined =>
  assetType?.inlineEmbedding?.mode

export const getInlineEmbeddableAssetTypes = (): AssetTypeDefinition[] =>
  assetTypes.filter((assetType) => !!assetType.inlineEmbedding)

export const getSupportedReferenceTypeIdsForAssetType = (assetType?: AssetTypeDefinition): string[] => {
  if (!assetType) return []
  if (assetType.id === 'config') {
    return getInlineEmbeddableAssetTypes().map((candidate) => candidate.id)
  }
  return assetType.supportedReferenceTypeIds?.filter(Boolean) ?? []
}

export { assetTypes }
export default assetTypes
